import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import {
  ArrowTurnUprightOutlinedIcon,
  ChartLineIcon,
  CheckIcon,
  EnterIcon,
  GoogleClassroomLogoIcon,
  PenPaperIcon,
  SquareStackIcon,
  TrashIcon,
} from '@/icons';
import type ZepIcon from '@/icons/ZepIcon';
import { useExternalPlatform } from '@/shared/externalPlatform';
import { type QuizListType } from '@/shared/quizList/QuizListType';
import { type CommonTranslationKeys } from '@/types/translation';
import type { ButtonProps } from '@/ui_v3';

const CommonQuizMenuTypes: QuizListType[] = ['public', 'featured', 'user'];

export const useGetQuizListItemButtons = (type: QuizListType) => {
  const { isInGoogleClassroom: inGoogleClassRoom, isInLti: inLti } =
    useExternalPlatform();

  return (quizSet: SearchQuizsetRes) => {
    if (inLti) {
      const ltiMenuButtons =
        type === 'myQuiz' ? ltiMyMenuButtons : [LtiSelectMenuItem];

      return quizSet.isLocked
        ? ltiMenuButtons.filter(item => item.onClickEventName !== 'lti_select')
        : ltiMenuButtons;
    }

    if (CommonQuizMenuTypes.includes(type)) {
      return [];
    }

    const googleMyMenuButtons: QuizCardMenuItem[] = [
      {
        label: 'googleClassroom.createAssignmentButton.text',
        icon: GoogleClassroomLogoIcon,
        intent: 'neutral',
        onClickEventName: 'google_assignment',
      },
    ];

    return inGoogleClassRoom
      ? googleMyMenuButtons
      : MyQuizCardMenuItems.filter(
          d => d.showGoogleClassButton === inGoogleClassRoom,
        ).map(d => ({
          ...d,
          disabled: d.onClickEventName === 'enter' ? quizSet.isLocked : false,
        }));
  };
};

export const LtiSelectMenuItem: QuizCardMenuItem = {
  label: 'moodle.selectQuizButton.text',
  icon: CheckIcon,
  intent: 'neutral',
  onClickEventName: 'lti_select',
};

export const MyQuizEditMenuItem: QuizCardMenuItem = {
  label: 'global.edit.button',
  icon: PenPaperIcon,
  onClickEventName: 'update',
  excludeHoverMenu: true,
  showGoogleClassButton: false,
};

const ltiMyMenuButtons: QuizCardMenuItem[] = [
  MyQuizEditMenuItem,
  LtiSelectMenuItem,
];

export const MyQuizCardMenuItems: QuizCardMenuItem[] = [
  {
    label: 'global.enter.button',
    icon: EnterIcon,
    onClickEventName: 'enter',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
  {
    label: 'global.share.button',
    icon: ArrowTurnUprightOutlinedIcon,
    onClickEventName: 'share',
    intent: 'neutral',
    variant: 'contained',
    showGoogleClassButton: false,
  },
  {
    label: 'global.duplicate.button',
    icon: SquareStackIcon,
    variant: 'contained',
    intent: 'neutral',
    onClickEventName: 'copy',
    showGoogleClassButton: false,
  },
  MyQuizEditMenuItem,
  {
    label: 'lmsBtn',
    icon: ChartLineIcon,
    onClickEventName: 'report',
    variant: 'outlined',
    intent: 'alternative',
    hidePortableDevice: true,
    showGoogleClassButton: false,
  },
  {
    label: 'global.delete',
    icon: TrashIcon,
    onClickEventName: 'delete',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
];

export type QuizCardMenuItem = {
  icon: ReturnType<typeof ZepIcon>;
  label: CommonTranslationKeys;
  variant?: ButtonProps['variant'];
  intent?: ButtonProps['intent'];
  disabled?: boolean;
  onClickEventName:
    | 'share'
    | 'copy'
    | 'report'
    | 'enter'
    | 'update'
    | 'delete'
    | 'google_assignment'
    | 'lti_select';
  excludeHoverMenu?: boolean;
  hidePortableDevice?: boolean;
  showGoogleClassButton?: boolean;
};
